import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { Alert } from "@/src/lib/productAlert";
import { Ionicons } from "@/src/components/ui/ionicons";
import { ReturnToLatestButton } from "@/src/components/ReturnToLatestButton";
import { useActiveAccount } from "@/src/hooks/useActiveAccount";
import { useConversationScroll } from "@/src/hooks/useConversationScroll";
import { createChatMessageId, toMillis } from "@/src/lib/chat";
import {
  markIdentityGroupRead,
  sendIdentityGroupMessage,
  setIdentityGroupPostingMode,
  subscribeIdentityGroupConversation,
  subscribeIdentityGroupMessages,
  type IdentityGroupConversation,
  type IdentityGroupMessage,
} from "@/src/lib/identityGroups";
import { reportIdentityGroupMessage } from "@/src/lib/identityMessagingSafety";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import { router, useLocalSearchParams } from "expo-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const db = getFirestore();
const BG = surface.secondary;
const CARD = surface.primary;
const BORDER = border.subtle;
const ACCENT = brand.primary;

function formatTime(value: any) {
  const ms = toMillis(value);
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export default function IdentityGroupThreadScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const groupId = useMemo(() => String(id || ""), [id]);
  const { activeAccountId } = useActiveAccount();
  const [group, setGroup] = useState<any>(null);
  const [conversation, setConversation] = useState<IdentityGroupConversation | null>(null);
  const [memberRole, setMemberRole] = useState<"owner" | "admin" | "member" | null>(null);
  const [messages, setMessages] = useState<IdentityGroupMessage[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [sending, setSending] = useState(false);
  const [postingModeBusy, setPostingModeBusy] = useState(false);
  const [safetyBusy, setSafetyBusy] = useState(false);
  const inverted = Platform.OS !== "web";
  const listMessages = useMemo(() => inverted ? [...messages].reverse() : messages, [inverted, messages]);
  const { listRef, onScroll, onLayout, onContentSizeChange, returnToLatest, showReturnToLatest, followLatest, cancelFollow } =
    useConversationScroll<IdentityGroupMessage>(`${activeAccountId || ""}:${groupId}`, messages.at(-1)?.id, inverted);

  useEffect(() => {
    if (!groupId) return;
    return onSnapshot(
      doc(db, "groups", groupId),
      (snap) => setGroup(snap.exists() ? { id: snap.id, ...(snap.data() as any) } : null),
      () => setGroup(null)
    );
  }, [groupId]);

  useEffect(() => {
    if (!groupId || !activeAccountId) {
      setMemberRole(null);
      return;
    }
    return onSnapshot(
      doc(db, "groups", groupId, "members", activeAccountId),
      (snap) => {
        if (!snap.exists()) {
          setMemberRole(null);
          return;
        }
        const role = (snap.data() as any)?.role;
        setMemberRole(role === "owner" ? "owner" : role === "admin" ? "admin" : "member");
      },
      () => setMemberRole(null)
    );
  }, [activeAccountId, groupId]);

  useEffect(() => {
    if (!groupId || !activeAccountId) {
      setConversation(null);
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setDenied(false);
    const unsubConversation = subscribeIdentityGroupConversation(
      groupId,
      (next) => {
        setConversation(next);
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        if (error?.code === "permission-denied") setDenied(true);
      }
    );
    const unsubMessages = subscribeIdentityGroupMessages(
      groupId,
      (next) => {
        setMessages(next);
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        if (error?.code === "permission-denied") setDenied(true);
      }
    );
    void markIdentityGroupRead(activeAccountId, groupId).catch(() => {});
    return () => {
      unsubConversation();
      unsubMessages();
    };
  }, [activeAccountId, groupId]);

  useEffect(() => {
    if (!activeAccountId || !groupId || !messages.length) return;
    const latest = messages[messages.length - 1];
    if (latest.senderIdentityId !== activeAccountId) {
      void markIdentityGroupRead(activeAccountId, groupId).catch(() => {});
    }
  }, [activeAccountId, groupId, messages]);

  const isGroupManager = memberRole === "owner" || memberRole === "admin";
  const canPost =
    !!activeAccountId
    && !!conversation
    && (
      conversation.whoCanPost === "all_members"
      || isGroupManager
    );

  const send = useCallback(async () => {
    const value = text.trim();
    if (!value || !activeAccountId || !groupId || sending || !canPost) return;
    const messageId = createChatMessageId();
    try {
      setSending(true);
      setText("");
      followLatest(true);
      await sendIdentityGroupMessage({
        groupId,
        senderIdentityId: activeAccountId,
        messageId,
        text: value,
      });
    } catch {
      cancelFollow();
      setText(value);
    } finally {
      setSending(false);
    }
  }, [activeAccountId, canPost, cancelFollow, followLatest, groupId, sending, text]);

  const openGroup = useCallback(() => {
    if (!groupId) return;
    router.push({ pathname: "/group/[groupId]", params: { groupId } });
  }, [groupId]);

  const explainMessageReporting = useCallback(() => {
    Alert.alert(
      "Signaler un message",
      "Appuie longuement sur le message concerné pour l’envoyer à la modération avec sa preuve.",
      [{ text: "Compris" }]
    );
  }, []);

  const togglePostingMode = useCallback(() => {
    if (!activeAccountId || !conversation || !isGroupManager || postingModeBusy) return;

    const nextMode =
      conversation.whoCanPost === "admins_only" ? "all_members" : "admins_only";
    const title =
      nextMode === "admins_only"
        ? "Limiter les messages aux administrateurs ?"
        : "Autoriser tous les membres ?";
    const message =
      nextMode === "admins_only"
        ? "Seuls le propriétaire et les administrateurs du groupe pourront envoyer des messages. Les autres membres pourront continuer à lire la discussion."
        : "Tous les membres du groupe pourront de nouveau envoyer des messages dans cette discussion.";

    Alert.alert(title, message, [
      { text: "Annuler", style: "cancel" },
      {
        text: "Confirmer",
        onPress: async () => {
          try {
            setPostingModeBusy(true);
            await setIdentityGroupPostingMode(activeAccountId, groupId, nextMode);
          } catch {
            Alert.alert(
              "Erreur",
              "Impossible de modifier les permissions de la discussion pour le moment."
            );
          } finally {
            setPostingModeBusy(false);
          }
        },
      },
    ]);
  }, [activeAccountId, conversation, groupId, isGroupManager, postingModeBusy]);

  const openConversationMenu = useCallback(() => {
    const actions: any[] = [
      { text: "Annuler", style: "cancel" },
      { text: "Voir le groupe", onPress: openGroup },
    ];

    if (isGroupManager && conversation) {
      actions.push({
        text:
          conversation.whoCanPost === "admins_only"
            ? "Messages : tous les membres"
            : "Messages : admins seulement",
        onPress: togglePostingMode,
      });
    }

    actions.push({ text: "Signaler un message", onPress: explainMessageReporting });

    Alert.alert(
      "Discussion du groupe",
      "Choisis une action.",
      actions
    );
  }, [conversation, explainMessageReporting, isGroupManager, openGroup, togglePostingMode]);

  const reportMessage = useCallback(async (message: IdentityGroupMessage) => {
    if (!activeAccountId || !groupId || safetyBusy || message.senderIdentityId === activeAccountId) return;
    try {
      setSafetyBusy(true);
      await reportIdentityGroupMessage({
        reporterIdentityId: activeAccountId,
        groupId,
        messageId: message.id,
        reason: "other",
        details: "Signalement d’un message depuis une discussion de groupe",
      });
      Alert.alert("Message signalé", "Ce message a été transmis à la modération avec sa preuve.");
    } catch (error: any) {
      const code = String(error?.code || "");
      Alert.alert(
        "Signalement impossible",
        code.includes("already-exists")
          ? "Ce message a déjà été signalé."
          : "Impossible d’envoyer ce signalement pour le moment."
      );
    } finally {
      setSafetyBusy(false);
    }
  }, [activeAccountId, groupId, safetyBusy]);

  const confirmReportMessage = useCallback((message: IdentityGroupMessage) => {
    if (message.senderIdentityId === activeAccountId || safetyBusy) return;
    Alert.alert(
      "Message",
      "Choisis une action.",
      [
        { text: "Annuler", style: "cancel" },
        {
          text: "Signaler ce message",
          style: "destructive",
          onPress: () => void reportMessage(message),
        },
      ]
    );
  }, [activeAccountId, reportMessage, safetyBusy]);

  if (denied) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
        <View style={{ flex: 1, alignItems: "center", justifyContent: "center", padding: 24 }}>
          <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 18 }}>Accès refusé</Text>
          <Text style={{ color: uiText.muted, marginTop: 8, textAlign: "center" }}>
            L’identité active ne fait pas partie de ce groupe.
          </Text>
          <Pressable
            onPress={() => router.replace("/(tabs)/messages")}
            style={{ marginTop: 16, backgroundColor: ACCENT, borderRadius: 12, paddingHorizontal: 16, paddingVertical: 11 }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>Retour aux messages</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === "ios" ? "padding" : Platform.OS === "android" ? "height" : undefined}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10, paddingHorizontal: 16, paddingVertical: 10 }}>
          <Pressable
            onPress={() => router.replace("/(tabs)/messages")}
            accessibilityRole="button"
            accessibilityLabel="Retour aux messages"
            style={{ width: 44, height: 44, borderRadius: 12, borderWidth: 1, borderColor: BORDER, backgroundColor: CARD, alignItems: "center", justifyContent: "center" }}
          >
            <Ionicons name="chevron-back" size={20} color={uiText.primary} />
          </Pressable>
          <Pressable
            onPress={openGroup}
            accessibilityRole="button"
            accessibilityLabel="Ouvrir le groupe"
            style={{ flex: 1, minWidth: 0 }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 18 }} numberOfLines={1}>
              {group?.name || "Discussion du groupe"}
            </Text>
            <Text style={{ color: uiText.muted, marginTop: 2, fontSize: 12 }}>
              {conversation?.whoCanPost === "admins_only" ? "Admins uniquement" : "Tous les membres"}
            </Text>
          </Pressable>
          <Pressable
            onPress={openConversationMenu}
            disabled={safetyBusy || postingModeBusy}
            accessibilityRole="button"
            accessibilityLabel="Ouvrir le menu de la discussion"
            hitSlop={8}
            style={{ width: 44, height: 44, borderRadius: 12, borderWidth: 1, borderColor: BORDER, backgroundColor: CARD, alignItems: "center", justifyContent: "center", opacity: safetyBusy || postingModeBusy ? 0.6 : 1 }}
          >
            <Ionicons name="ellipsis-horizontal" size={20} color={uiText.primary} />
          </Pressable>
        </View>

        {loading && !messages.length ? (
          <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
            <ActivityIndicator color={ACCENT} />
          </View>
        ) : (
          <View style={{ flex: 1 }}>
          <FlatList
            ref={listRef}
            data={listMessages}
            inverted={inverted}
            keyExtractor={(item) => item.id}
            contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 12, flexGrow: 1, justifyContent: messages.length ? "flex-start" : "center" }}
            maintainVisibleContentPosition={inverted ? { minIndexForVisible: 0 } : undefined}
            onScroll={onScroll}
            onLayout={onLayout}
            scrollEventThrottle={32}
            onContentSizeChange={onContentSizeChange}
            ListEmptyComponent={<Text style={{ color: uiText.muted, textAlign: "center" }}>Aucun message pour le moment.</Text>}
            renderItem={({ item }) => {
              const mine = item.senderIdentityId === activeAccountId;
              const sender = item.senderSnapshot;
              return (
                <Pressable
                  onLongPress={!mine ? () => confirmReportMessage(item) : undefined}
                  delayLongPress={350}
                  accessibilityRole={!mine ? "button" : undefined}
                  accessibilityLabel={!mine ? "Message reçu. Appui long pour les actions." : undefined}
                  style={{ alignItems: mine ? "flex-end" : "flex-start", marginBottom: 10 }}
                >
                  {!mine ? (
                    <Pressable
                      onPress={() => {
                        if (item.senderIdentityId) {
                          router.push({ pathname: "/profile/[id]", params: { id: item.senderIdentityId } });
                        }
                      }}
                      accessibilityRole="button"
                      accessibilityLabel="Ouvrir le profil de l’auteur"
                    >
                      <Text style={{ color: uiText.brand, fontSize: 12, fontWeight: "800", marginBottom: 4 }}>
                        {sender?.displayName || (sender?.handle ? `@${sender.handle}` : "Identité")}
                      </Text>
                    </Pressable>
                  ) : null}
                  <View style={{ maxWidth: "78%", borderRadius: 16, borderWidth: 1, borderColor: mine ? surface.selected : BORDER, backgroundColor: mine ? ACCENT : CARD, paddingHorizontal: 12, paddingVertical: 10 }}>
                    <Text style={{ color: uiText.primary, lineHeight: 20 }}>{item.text}</Text>
                  </View>
                  <Text style={{ color: uiText.muted, fontSize: 11, marginTop: 4 }}>{formatTime(item.createdAt)}</Text>
                </Pressable>
              );
            }}
          />
          <ReturnToLatestButton visible={showReturnToLatest} onPress={returnToLatest} />
          </View>
        )}

        <View style={{ borderTopWidth: 1, borderTopColor: BORDER, padding: 12, flexDirection: "row", gap: 10, alignItems: "flex-end" }}>
          <BrandTextInput
            value={text}
            onChangeText={setText}
            editable={canPost && !sending}
            placeholder={
              canPost
                ? "Écrire un message…"
                : conversation?.whoCanPost === "admins_only"
                ? "Seuls les admins peuvent écrire."
                : "Discussion indisponible."
            }
            placeholderTextColor={uiText.muted}
            multiline
            onKeyPress={(event: any) => {
              if (
                Platform.OS === "web"
                && event?.nativeEvent?.key === "Enter"
                && !event?.nativeEvent?.shiftKey
              ) {
                event?.preventDefault?.();
                void send();
              }
            }}
            style={{ flex: 1, minHeight: 44, maxHeight: 120, color: uiText.primary, backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 12 }}
          />
          <Pressable
            onPress={() => void send()}
            disabled={!text.trim() || !canPost || sending}
            style={{ minWidth: 86, height: 44, borderRadius: 12, backgroundColor: text.trim() && canPost && !sending ? ACCENT : surface.selected, alignItems: "center", justifyContent: "center", paddingHorizontal: 14 }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>{sending ? "Envoi…" : "Envoyer"}</Text>
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
